import cn from 'classnames'
import sanitizeHtml from 'sanitize-html'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { getUrlWithParams } from 'core-components/helpers/url'
import { IColumn } from 'core-components/library/list/types'
import { FinancialBeautifiedValue } from 'partner360/components/financialBeautifiedValue/FinancialBeautifiedValue'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'
import { getValueTypeValue } from 'partner360/widgets/gp360Administration'
import { TRANSLATION_KEYS } from 'partner360/widgets/gp360Administration/translations'

import { GlobalPartnerFinancialColumnTitle } from '../../../../../helpers/GlobalPartnerFinancialColumnTitle'
import { IGlobalPartnerDetails } from '../../../../../models/globalPartnerDetails'
import styles from '../TableContent.module.scss'

export const getTableColumn = (
  fYear: number,
  valueType?: ActualsValueTypes
): IColumn<IGlobalPartnerDetails>[] => [
  {
    field: 'Geo',
    title: TRANSLATION_KEYS.tableColumns.globalPartnerGeo,
    hideSettings: true,
    disableSort: true,
    renderFunction: (item, column, rowAttrs) => {
      return (
        <div
          style={{
            paddingLeft: rowAttrs && rowAttrs.deep ? `${rowAttrs.deep * 16}px` : undefined,
          }}
        >
          {item.Geo}
        </div>
      )
    },
  },
  {
    field: '',
    title: TRANSLATION_KEYS.tableColumns.globalPartnerData,
    hideSettings: true,
    disableSort: true,
    renderFunction: (item) => {
      if (item.PartnerId === null) {
        return (
          <Translation
            text={TRANSLATION_KEYS.tableColumns.globalPartnerDataContent}
            replaceParams={[
              { key: '{partnersCount}', value: String(item.PartnersCount) },
              { key: '{sumUp}', value: String(item.IsActualsSumUp) },
              { key: '{requiredPlan}', value: String(item.IsPlanRequired) },
            ]}
          />
        )
      }

      return (
        <div className={styles.partnerDetailsWrapper}>
          <a
            target="_blank"
            href={sanitizeHtml(getUrlWithParams({ page: 'jbl-hpscoutso', pid: item.PartnerId }))}
            rel="noreferrer"
          >
            <div className={styles.rowData}>
              <span>{item.PartnerName}</span>
            </div>
            <div className={styles.rowData}>
              <span className={styles.partnerId}>{item.PartnerId}</span>
              <span>{item.LocationId}</span>
            </div>
          </a>
          <RenderWhen condition={Boolean(item.IsPlanRequired)}>
            <div className={styles.smallInfo}>
              <Translation text={TRANSLATION_KEYS.tableColumns.isPlanRequired} />
            </div>
          </RenderWhen>
          <RenderWhen condition={Boolean(item.IsActualsSumUp)}>
            <div className={styles.smallInfo}>
              <Translation text={TRANSLATION_KEYS.tableColumns.isActualsSumUp} />
            </div>
          </RenderWhen>
        </div>
      )
    },
  },
  {
    field: 'TotalCompleteness',
    title: TRANSLATION_KEYS.tableColumns.globalTotalCompleted,
    hideSettings: true,
    disableSort: true,
    renderFunction: (item) => {
      const isPlanRequiredValid = item.IsPlanRequired > 0

      const totalCompletedToTotalCount = isPlanRequiredValid
        ? ((item.TotalCompleteness / item.IsPlanRequired) * 100).toFixed(0)
        : '0'
      if (item.PartnerId !== null) {
        return <>{`${item.TotalCompleteness}%`}</>
      }
      return (
        <td className={styles.totalCompleteness}>
          <div>
            {item.TotalCompleteness} / {totalCompletedToTotalCount}%
          </div>
        </td>
      )
    },
  },
  {
    field: 'FullyAcknowldgedCount',
    title: TRANSLATION_KEYS.tableColumns.globalFullyAcknowledged,
    hideSettings: true,
    disableSort: true,
    renderFunction: (item) => {
      const isPlanRequiredValid = item.IsPlanRequired > 0

      const fullyAcknowldgedToTotalCount = isPlanRequiredValid
        ? ((item.FullyAcknowldgedCount / item.IsPlanRequired) * 100).toFixed(0)
        : '0'

      if (item.PartnerId !== null) {
        if (item.IsFullyAcknowledged === 1) {
          return (
            <div>
              <i className={cn(styles.icon, 'kbase kbase-check')} />
            </div>
          )
        }
        return (
          <div>
            <i className={cn(styles.icon__close, 'kbase kbase-close')} />
          </div>
        )
      }
      return (
        <td>
          <div>
            {item.FullyAcknowldgedCount} / {fullyAcknowldgedToTotalCount}%
          </div>
        </td>
      )
    },
  },
  {
    field: 'PartnersPrevFYearActuals',
    title: '',
    hideSettings: true,
    disableSort: true,
    titleRender: () => {
      const translationKey = TRANSLATION_KEYS.tableColumns.globalPartnerPrevYear
      return (
        <GlobalPartnerFinancialColumnTitle
          translationKey={translationKey}
          valueType={valueType}
          fYear={fYear - 1}
        />
      )
    },
    renderFunction: (item) => {
      const value = getValueTypeValue({
        valueType,
        defaultValue: item.PartnersPrevFYearActualsNdp,
        netValue: item.PartnersPrevFYearActualsNet,
        tnetValue: item.PartnersPrevFYearActualsTNet,
        utnetValue: item.PartnersPrevFYearActualsUTNet,
        ndpValue: item.PartnersPrevFYearActualsNdp,
      })
      return (
        <FinancialBeautifiedValue
          className={styles.financialValue}
          value={value}
          minimumFractionDigits={0}
          maximumFractionDigits={0}
        />
      )
    },
  },
  {
    field: 'PartnersCurrentFYearTargets',
    title: '',
    hideSettings: true,
    disableSort: true,
    titleRender: () => {
      const translationKey = TRANSLATION_KEYS.tableColumns.globalPartnerFinAim
      return (
        <GlobalPartnerFinancialColumnTitle
          translationKey={translationKey}
          valueType={valueType}
          fYear={fYear}
        />
      )
    },
    renderFunction: (item) => {
      const value = getValueTypeValue({
        valueType,
        defaultValue: item.PartnersCurrentFYearTargetsNdp,
        netValue: item.PartnersCurrentFYearTargetsNet,
        tnetValue: item.PartnersCurrentFYearTargetsTNet,
        utnetValue: item.PartnersCurrentFYearTargetsUTNet,
        ndpValue: item.PartnersCurrentFYearTargetsNdp,
      })
      return (
        <FinancialBeautifiedValue
          className={styles.financialValue}
          value={value}
          minimumFractionDigits={0}
          maximumFractionDigits={0}
        />
      )
    },
  },
  {
    field: 'PartnerCurrentFYearActuals',
    title: '',
    hideSettings: true,
    disableSort: true,
    titleRender: () => {
      const translationKey = TRANSLATION_KEYS.tableColumns.globalPartnerCurrYear
      return (
        <GlobalPartnerFinancialColumnTitle
          translationKey={translationKey}
          valueType={valueType}
          fYear={fYear}
        />
      )
    },
    renderFunction: (item) => {
      const value = getValueTypeValue({
        valueType,
        defaultValue: item.PartnerCurrentFYearActualsNdp,
        netValue: item.PartnerCurrentFYearActualsNet,
        tnetValue: item.PartnerCurrentFYearActualsTNet,
        utnetValue: item.PartnerCurrentFYearActualsUTNet,
        ndpValue: item.PartnerCurrentFYearActualsNdp,
      })
      return (
        <FinancialBeautifiedValue
          className={styles.financialValue}
          value={value}
          minimumFractionDigits={0}
          maximumFractionDigits={0}
        />
      )
    },
  },
]
